class ProductHeroVariants {
  constructor(form) {
    this.form = form;
    this.sectionId = form.dataset.sectionId || '';
    this.section =
      document.getElementById('shopify-section-' + this.sectionId) ||
      form.closest('.shopify-section') ||
      document;

    const jsonEl = document.getElementById('product-json-' + this.sectionId);
    if (!jsonEl) return;

    try {
      this.productData = JSON.parse(jsonEl.textContent || '{}');
      const preorderEl = document.getElementById('product-preorders-' + this.sectionId);
      this.preorders = JSON.parse(preorderEl?.textContent || '{}');
    } catch (_) {
      return;
    }

    this.variants = Array.isArray(this.productData.variants)
      ? this.productData.variants
      : [];

    this.variantIdInput = form.querySelector('[data-variant-id-input]');
    this.preorderProperty = form.querySelector('[data-preorder-property]');
    this.preorderNote = form.querySelector('[data-preorder-note]');
    this.priceEl = this.section.querySelector('.product-hero__title-price');
    this.productTitle = this.section.querySelector('.product-hero__title');
    this.addToBagBtn = form.querySelector('[data-add-to-bag]');
    this.addToBagText = form.querySelector('.product-hero__add-to-bag-text');
    this.addToBagPrice = form.querySelector('.product-hero__add-to-bag-price');
    this.addToBagStatus = form.querySelector('[data-add-to-bag-status]');
    this.quantityEl = form.querySelector('[data-product-hero-quantity]');
    this.quantityInput = form.querySelector('[data-quantity-input]');
    this.decrementBtn = form.querySelector('[data-quantity-decrement]');
    this.incrementBtn = form.querySelector('[data-quantity-increment]');

    this.currency = this.priceEl ? this.priceEl.dataset.currency : 'AUD';
    this.locale = document.documentElement.lang || 'en';

    this._onChange = this._onChange.bind(this);
    this._onSubmit = this._onSubmit.bind(this);

    this._getOptionInputs().forEach((input) => {
      input.addEventListener('change', this._onChange);
    });
    this.form.addEventListener('submit', this._onSubmit);

    if (this.decrementBtn) {
      this.decrementBtn.addEventListener('click', () => this._changeQuantity(-1));
    }
    if (this.incrementBtn) {
      this.incrementBtn.addEventListener('click', () => this._changeQuantity(1));
    }

    // The server-resolved variant includes ?variant= links, including sold-out
    // sizes. Initialise the radios from it before applying the selection.
    const initialVariant = this._getVariantById(this.variantIdInput?.value);
    if (initialVariant) {
      this._getOptionGroups().forEach((group) => {
        const value = initialVariant.options[Number(group.dataset.optionIndex)];
        const input = Array.from(group.querySelectorAll('.variant-option__input')).find(
          (candidate) => candidate.value === value
        );
        if (input) this._setCheckedInGroup(group, input);
      });
    }
    this._enforceSingleCheckedPerGroup();
    this._applyFromSelection(null, false);
  }

  _getOptionGroups() {
    return Array.from(this.section.querySelectorAll('[data-option-index]')).sort(
      (a, b) =>
        parseInt(a.dataset.optionIndex, 10) - parseInt(b.dataset.optionIndex, 10)
    );
  }

  _getOptionInputs() {
    return this._getOptionGroups().flatMap((group) =>
      Array.from(group.querySelectorAll('.variant-option__input'))
    );
  }

  _getSelectedOptions() {
    return this._getOptionGroups().map((group) => {
      const checked = group.querySelector('.variant-option__input:checked');
      return checked ? checked.value : '';
    });
  }

  _findVariantByOptions(selectedOptions) {
    return (
      this.variants.find(
        (variant) =>
          Array.isArray(variant.options) &&
          variant.options.length === selectedOptions.length &&
          variant.options.every((value, index) => value === selectedOptions[index])
      ) || null
    );
  }

  _getVariantById(id) {
    if (!id) return null;
    return this.variants.find((variant) => String(variant.id) === String(id)) || null;
  }

  _onChange(event) {
    const input = event.target;
    if (!input || !input.classList.contains('variant-option__input')) return;
    if (input.disabled) return;

    const group = input.closest('[data-option-index]');
    if (!group) return;

    this._setCheckedInGroup(group, input);
    this._applyFromSelection(input, true);
  }

  _setCheckedInGroup(group, keepInput) {
    Array.from(group.querySelectorAll('.variant-option__input')).forEach((input) => {
      input.checked = input === keepInput;
    });
  }

  _enforceSingleCheckedPerGroup() {
    this._getOptionGroups().forEach((group) => {
      const checked = group.querySelector('.variant-option__input:checked');
      if (checked) {
        this._setCheckedInGroup(group, checked);
        return;
      }

      // Required default: first option in each row selected.
      const first = group.querySelector('.variant-option__input');
      if (first) {
        this._setCheckedInGroup(group, first);
      }
    });
  }

  _applyFromSelection(changedInput, announce) {
    const selectedOptions = this._getSelectedOptions();
    let variant = this._findVariantByOptions(selectedOptions);

    if (!variant && this.variantIdInput) {
      variant = this._getVariantById(this.variantIdInput.value);
    }
    if (!variant) {
      variant = this.variants[0] || null;
    }
    if (!variant) return;

    if (this.variantIdInput) {
      this.variantIdInput.value = variant.id;
    }

    this._updatePrice(variant);
    this._updateAddToBag(variant);
    this._updateQuantity(variant);
    this._updateURL(variant);
    this._updateSelectedValueLabels();
    this._syncAvailabilityFromSelection();
    this._notifyCarousel(variant);
    this._notifySizeComparison(variant);

    if (announce && changedInput) {
      this._announceVariantChange(changedInput, variant);
    }
  }

  _syncAvailabilityFromSelection() {
    const selected = this._getSelectedOptions();

    this._getOptionGroups().forEach((group) => {
      const axisIndex = parseInt(group.dataset.optionIndex, 10);

      Array.from(group.querySelectorAll('.variant-option__input')).forEach((input) => {
        const test = selected.slice();
        test[axisIndex] = input.value;

        const match = this._findVariantByOptions(test);
        const available = Boolean(match && match.available);
        const option = input.closest('.variant-option');
        const label = group.querySelector('label[for="' + input.id + '"]');

        if (available) {
          input.disabled = false;
          input.removeAttribute('aria-disabled');
          input.removeAttribute('data-unavailable');
          if (option) option.classList.remove('variant-option--unavailable');
          if (label) label.classList.remove('variant-option__label--unavailable');
          this._removeSoldOutLabel(label);
        } else {
          // IXIA sizes remain viewable when sold out; purchasing is still
          // blocked by the selected variant's Add to Bag state.
          const canViewSize = this.productData.handle === 'ixia';
          input.disabled = !canViewSize;
          if (canViewSize) input.removeAttribute('aria-disabled');
          else input.setAttribute('aria-disabled', 'true');
          input.setAttribute('data-unavailable', '');
          if (input.checked && !canViewSize) {
            const firstEnabled = Array.from(
              group.querySelectorAll('.variant-option__input')
            ).find((el) => !el.disabled && el !== input);
            if (firstEnabled) {
              this._setCheckedInGroup(group, firstEnabled);
            }
          }
          if (option) option.classList.add('variant-option--unavailable');
          if (label) {
            label.classList.add('variant-option__label--unavailable');
            this._ensureSoldOutLabel(label);
          }
        }
      });
    });
  }

  _updateSelectedValueLabels() {
    this._getOptionGroups().forEach((group) => {
      const selected = group.querySelector('.variant-option__input:checked');
      const label = group.querySelector('[data-selected-value]');
      if (selected && label) {
        label.textContent = selected.dataset.optionLabel || selected.value;
      }
    });
  }

  _updatePrice(variant) {
    if (!this.priceEl) return;
    this.priceEl.innerHTML = this._buildPriceHTML(variant);
  }

  _updateAddToBag(variant) {
    const isPreorder = variant.available && this.preorders[variant.id] === true;
    if (this.preorderProperty) this.preorderProperty.disabled = !isPreorder;
    if (this.preorderNote) {
      this.preorderNote.hidden = !isPreorder;
      if (isPreorder) this.addToBagBtn?.setAttribute('aria-describedby', this.preorderNote.id);
      else this.addToBagBtn?.removeAttribute('aria-describedby');
    }
    if (!this.addToBagBtn || !this.addToBagText) return;

    const unavailable = !variant.available;
    const title = this.productTitle ? this.productTitle.textContent.trim() : 'Product';

    if (unavailable) {
      this.addToBagBtn.disabled = true;
      this.addToBagBtn.setAttribute('aria-disabled', 'true');
      this.addToBagBtn.setAttribute('aria-label', title + ' is sold out');
      this.addToBagText.textContent = 'Sold out';
      if (this.addToBagPrice) {
        this.addToBagPrice.remove();
        this.addToBagPrice = null;
      }
      return;
    }

    const formattedPrice = this._formatMoney(variant.price);
    this.addToBagBtn.disabled = false;
    this.addToBagBtn.removeAttribute('aria-disabled');
    this.addToBagBtn.setAttribute('aria-label', isPreorder
      ? 'Preorder ' + title + ', ' + formattedPrice
      : 'Add ' + title + ' to cart, ' + formattedPrice);
    this.addToBagText.textContent = isPreorder ? 'Preorder' : 'Add to Bag';

    if (!this.addToBagPrice) {
      this.addToBagPrice = document.createElement('span');
      this.addToBagPrice.className = 'product-hero__add-to-bag-price type-h4';
      this.addToBagPrice.setAttribute('aria-hidden', 'true');
      this.addToBagBtn.appendChild(this.addToBagPrice);
    }

    this.addToBagPrice.textContent = formattedPrice;
  }

  _updateQuantity(variant) {
    if (!this.quantityEl) return;
    if (!variant.available) {
      this.quantityEl.setAttribute('hidden', '');
      return;
    }
    this.quantityEl.removeAttribute('hidden');
    if (this.quantityInput) {
      this.quantityInput.value = '1';
    }
  }

  _updateURL(variant) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState(null, '', url.toString());
  }

  _notifyCarousel(variant) {
    if (!variant.featured_image) return;
    document.dispatchEvent(
      new CustomEvent('hattie:variant-image-change', {
        bubbles: true,
        detail: { imageId: variant.featured_image.id },
      })
    );
  }

  _notifySizeComparison(variant) {
    if (this.productData.handle !== 'ixia') return;
    const sizeIndex = this.productData.options.findIndex(
      (option) => String(option.name || option).toLowerCase() === 'size'
    );
    const size = String(variant.options[sizeIndex] || '').toLowerCase().split(' ')[0];
    this.section.querySelectorAll('.vases-range-rows__row[data-vase-size]').forEach((row) => {
      row.classList.toggle('vases-range-rows__row--current', row.dataset.vaseSize === size);
    });
    document.dispatchEvent(new CustomEvent('hattie:variant-size-change', {
      detail: { productId: this.productData.id, size },
    }));
  }

  async _onSubmit(event) {
    event.preventDefault();
    if (!this.addToBagBtn || this.addToBagBtn.disabled) return;

    const variantId = this.variantIdInput ? this.variantIdInput.value : '';
    const quantity = this.quantityInput ? parseInt(this.quantityInput.value, 10) || 1 : 1;
    if (!variantId) return;

    this.addToBagBtn.disabled = true;
    this.addToBagBtn.classList.add('is-loading');
    const originalText = this.addToBagText ? this.addToBagText.textContent : 'Add to Bag';
    if (this.addToBagText) this.addToBagText.textContent = 'Adding...';

    try {
      if (typeof addToCart === 'function') {
        const properties = this.preorderProperty && !this.preorderProperty.disabled
          ? { 'Order type': this.preorderProperty.value }
          : {};
        await addToCart(variantId, quantity, properties);
      } else {
        this.form.submit();
      }
    } catch (_) {
      if (this.addToBagStatus) {
        this.addToBagStatus.setAttribute('role', 'alert');
        this.addToBagStatus.textContent = 'Could not add item to cart.';
      }
    } finally {
      this.addToBagBtn.classList.remove('is-loading');
      if (this.addToBagText) this.addToBagText.textContent = originalText;
      if (!this.addToBagBtn.hasAttribute('aria-disabled')) this.addToBagBtn.disabled = false;
    }
  }

  _changeQuantity(delta) {
    if (!this.quantityInput) return;
    const min = parseInt(this.quantityInput.min, 10) || 1;
    const current = parseInt(this.quantityInput.value, 10) || min;
    const next = Math.max(min, current + delta);
    if (next === current) return;
    this.quantityInput.value = String(next);
    if (typeof announceToScreenReader === 'function') {
      announceToScreenReader('Quantity ' + next);
    }
  }

  _announceVariantChange(input, variant) {
    if (typeof announceToScreenReader !== 'function') return;
    const group = input.closest('[data-option-index]');
    const optionName = group ? group.querySelector('.variant-fieldset__option-name') : null;
    const prefix = optionName ? optionName.textContent.trim() + ': ' : '';
    announceToScreenReader(
      prefix +
        (input.dataset.optionLabel || input.value) +
        (variant.available ? (this.preorders[variant.id] === true ? ', preorder' : '') : ', sold out') +
        ', ' +
        this._formatMoney(variant.price)
    );
  }

  _ensureSoldOutLabel(label) {
    if (!label) return;
    const existing = label.querySelector('.visually-hidden[data-sold-out]');
    if (existing) return;
    const span = document.createElement('span');
    span.className = 'visually-hidden';
    span.setAttribute('data-sold-out', '');
    span.textContent = ' — sold out';
    label.appendChild(span);
  }

  _removeSoldOutLabel(label) {
    if (!label) return;
    label.querySelectorAll('.visually-hidden[data-sold-out]').forEach((el) => el.remove());
  }

  _formatMoney(cents) {
    try {
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency,
        trailingZeroDisplay: 'stripIfInteger',
      }).format(cents / 100);
    } catch (_) {
      return (cents / 100).toFixed(2);
    }
  }

  _buildPriceHTML(variant) {
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      return (
        '<span class="product-hero__price-sale">' +
          '<span class="visually-hidden">Sale price: </span>' +
          this._escapeHTML(this._formatMoney(variant.price)) +
        '</span>' +
        '<s class="product-hero__price-compare">' +
          '<span class="visually-hidden">Regular price: </span>' +
          this._escapeHTML(this._formatMoney(variant.compare_at_price)) +
        '</s>'
      );
    }

    return (
      '<p class="product-hero__price type-h2">' +
        '<span class="visually-hidden">Price: </span>' +
        this._escapeHTML(this._formatMoney(variant.price)) +
      '</p>'
    );
  }

  _escapeHTML(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

function initProductHeroVariants() {
  document.querySelectorAll('form[data-product-form]').forEach((form) => {
    if (form.dataset.productHeroVariantsInitialized) return;
    form.dataset.productHeroVariantsInitialized = 'true';
    new ProductHeroVariants(form);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductHeroVariants);
} else {
  initProductHeroVariants();
}

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', initProductHeroVariants);
}
